import { isDevelopment } from 'src/module/helpers';
import api from './api';
import firebaseService from './firebase';

class DeviceService {
    constructor() {
        this.currentSession = null;
        this.isDeviceRegistered = false;
    }

    /**
     * Initialize device registration flow
     */
    async initializeDevice(userId) {
        try {
            console.log('[DEVICE] Initializing device...');

            // 1. Cek token validitas
            const token = await firebaseService.getToken();

            if (!token) {
                console.warn('[DEVICE] No FCM token available');
                return { success: false, reason: 'no_token' };
            }

            // 2. Register device ke backend
            const registerResponse = await this.registerDevice(token);

            if (!registerResponse.success) {
                return registerResponse;
            }

            // 3. Login device untuk user yang sedang aktif
            if (userId) {
                const loginResponse = await this.loginToDevice(userId, token);

                console.log(loginResponse)

                if (loginResponse.success) {
                    this.isDeviceRegistered = true;
                    this.currentSession = loginResponse.data.session;

                    // Simpan session info
                    localStorage.setItem('device_session', JSON.stringify({
                        sessionId: loginResponse.data.session?.id,
                        deviceId: loginResponse.data.device?.id,
                        loggedInAt: new Date().toISOString()
                    }));
                }

                return loginResponse;
            }

            return registerResponse;

        } catch (error) {
            console.error('[DEVICE] Initialize failed:', error);
            return {
                success: false,
                message: error.message,
                error
            };
        }
    }

    /**
     * Register device ke backend
     */
    async registerDevice(fcmToken) {
        try {
            const response = await api.post('/multi-device/register', {
                fcmToken,
                deviceIdentifier: this.getDeviceIdentifier(),
                userAgent: navigator.userAgent,
                appVersion: process.env.REACT_APP_VERSION || '1.0.0'
            });

            if (isDevelopment)
                console.log('[DEVICE] Device registered:', response.data);

            return {
                success: true,
                data: response.data,
                deviceId: response.data.deviceId
            };

        } catch (error) {
            if (isDevelopment)
                console.error('[DEVICE] Register failed:', error);

            // Handle specific errors
            if (error.response?.status === 409) {
                // Device already registered
                return {
                    success: true,
                    message: 'Device already registered',
                    data: error.response.data
                };
            }

            return {
                success: false,
                message: 'Failed to register device',
                error: error.response?.data || error.message
            };
        }
    }

    /**
     * Login user ke device
     */
    async loginToDevice(userId, fcmToken) {
        try {
            const sessionId = this.generateSessionId(userId);

            const response = await api.post('/multi-device/login', {
                fcmToken,
                sessionId,
                deviceInfo: this.getDeviceInfo(),
                userAgent: navigator.userAgent
            });

            if (isDevelopment)

                console.log('[DEVICE] User logged into device:', response.data);

            return {
                success: true,
                data: response.data
            };

        } catch (error) {
            if (isDevelopment)
                console.error('[DEVICE] Login failed:', error);

            return {
                success: false,
                message: 'Failed to login to device',
                error: error.response?.data || error.message
            };
        }
    }

    /**
     * Logout user dari device
     */
    async logoutFromDevice() {
        try {
            const session = this.getCurrentSession();
            if (!session) {
                return { success: false, message: 'No active session' };
            }

            const fcmToken = localStorage.getItem('fcm_token');
            if (!fcmToken) {
                return { success: false, message: 'No FCM token found' };
            }

            const response = await api.post('/multi-device/logout', {
                fcmToken,
                sessionId: session.sessionId
            });

            if (isDevelopment)
                console.log('[DEVICE] User logged out from device');

            // Clear local session
            this.clearLocalSession();

            return {
                success: true,
                data: response.data
            };

        } catch (error) {
            if (isDevelopment)
                console.error('[DEVICE] Logout failed:', error);

            // Force clear local session even if API fails
            this.clearLocalSession();

            return {
                success: false,
                message: 'Logout failed but local session cleared',
                error: error.response?.data || error.message
            };
        }
    }

    /**
     * Get current device session
     */
    getCurrentSession() {
        try {
            const sessionStr = localStorage.getItem('device_session');
            return sessionStr ? JSON.parse(sessionStr) : null;
        } catch {
            return null;
        }
    }

    /**
     * Clear local session data
     */
    clearLocalSession() {
        localStorage.removeItem('device_session');
        this.currentSession = null;
        this.isDeviceRegistered = false;

        // Clear FCM token
        firebaseService.clearToken();

        console.log('[DEVICE] Local session cleared');
    }

    /**
     * Get device info untuk tracking
     */
    getDeviceInfo() {
        return {
            screenSize: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            platform: navigator.platform,
            cookiesEnabled: navigator.cookieEnabled,
            doNotTrack: navigator.doNotTrack || 'unspecified',
            hardwareConcurrency: navigator.hardwareConcurrency,
            deviceMemory: navigator.deviceMemory || 'unknown',
            connection: navigator.connection ? {
                effectiveType: navigator.connection.effectiveType,
                downlink: navigator.connection.downlink,
                rtt: navigator.connection.rtt
            } : null
        };
    }

    /**
     * Generate unique device identifier
     */
    getDeviceIdentifier() {
        // Try to get from localStorage first
        let deviceId = localStorage.getItem('device_id');

        if (!deviceId) {
            // Generate new ID
            deviceId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('device_id', deviceId);
        }

        return deviceId;
    }

    /**
     * Generate session ID
     */
    generateSessionId(userId) {
        return `session_${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Check if device is registered and logged in
     */
    isLoggedIn() {
        const session = this.getCurrentSession();
        const fcmToken = localStorage.getItem('fcm_token');

        return !!(session && fcmToken);
    }

    /**
     * Get device status summary
     */
    getStatus() {
        return {
            isRegistered: this.isDeviceRegistered,
            isLoggedIn: this.isLoggedIn(),
            hasFcmToken: !!localStorage.getItem('fcm_token'),
            session: this.getCurrentSession() || {},
            firebaseState: firebaseService.getState()
        };
    }

    /**
     * Handle app start - auto initialize device
     */
    async handleAppStart(userId) {
        // Cek permission status
        const permission = Notification.permission;

        if (permission === 'granted') {
            // Auto initialize jika permission sudah diberikan
            return this.initializeDevice(userId);
        } else if (permission === 'default') {
            // Permission belum diminta, tunggu user action
            if (isDevelopment)
                console.log('[DEVICE] Waiting for user to grant permission');
            return { success: false, reason: 'permission_needed' };
        } else {
            // Permission denied
            if (isDevelopment)
                console.log('[DEVICE] Permission denied by user');
            return { success: false, reason: 'permission_denied' };
        }
    }
}

// Singleton instance
const deviceService = new DeviceService();
export default deviceService;